<!--
  #534: the gear is deliberately quiet chrome — no surface, no accent, colour-only hover. The panel is the one
  elevated thing here, and the switch's on-state is the only colour. `relative` is the panel's anchor.
-->
<!--
  `flex` (not `inline-block`): an inline-block inside the inline host would sit on a text baseline and pick up
  descender space below the glyph, which showed as the gear riding a few px above neighbouring icons and text.
  The host itself is `inline-flex` (see the scss) for the same reason.
-->
<div class="relative flex items-center">
  <!--
    A 44px tap target (`h-11 w-11` + `p-2.5`) around a 24px glyph, pulled back to a 24px layout footprint with
    `-m-2.5` so a header does not grow taller just to host it. 24px, not the card icons' 20px: Bootstrap's gear
    glyph fills only ~80% of its viewBox, so at 20px it read as visibly smaller than the 20px arrows beside it.
  -->
  <button #trigger type="button"
          class="-m-2.5 inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg p-2.5
                 text-gray-500 transition-colors duration-200 hover:text-gray-800 focus-visible:outline
                 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-theme-primary-500"
          [class.text-gray-800]="open()"
          aria-label="Anzeigeoptionen"
          aria-haspopup="dialog"
          [attr.aria-expanded]="open()"
          [attr.aria-controls]="panelId()"
          (click)="onTriggerClick($event)">
    <svg class="bi bi-gear h-6 w-6" height="24" width="24" viewBox="0 0 16 16" aria-hidden="true"
         xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 4.754a3.246 3.246 0 1 0 0 6.492 3.246 3.246 0 0 0 0-6.492M5.754 8a2.246 2.246 0 1 1 4.492 0 2.246 2.246 0 0 1-4.492 0"/>
      <path
        d="M9.796 1.343c-.527-1.79-3.065-1.79-3.592 0l-.094.319a.873.873 0 0 1-1.255.52l-.292-.16c-1.64-.892-3.433.902-2.54 2.541l.159.292a.873.873 0 0 1-.52 1.255l-.319.094c-1.79.527-1.79 3.065 0 3.592l.319.094a.873.873 0 0 1 .52 1.255l-.16.292c-.892 1.64.901 3.434 2.541 2.54l.292-.159a.873.873 0 0 1 1.255.52l.094.319c.527 1.79 3.065 1.79 3.592 0l.094-.319a.873.873 0 0 1 1.255-.52l.292.16c1.64.893 3.434-.902 2.54-2.541l-.159-.292a.873.873 0 0 1 .52-1.255l.319-.094c1.79-.527 1.79-3.065 0-3.592l-.319-.094a.873.873 0 0 1-.52-1.255l.16-.292c.893-1.64-.902-3.433-2.541-2.54l-.292.159a.873.873 0 0 1-1.255-.52zm-2.633.283c.246-.835 1.428-.835 1.674 0l.094.319a1.873 1.873 0 0 0 2.693 1.115l.291-.16c.764-.415 1.6.42 1.184 1.185l-.159.292a1.873 1.873 0 0 0 1.116 2.692l.318.094c.835.246.835 1.428 0 1.674l-.319.094a1.873 1.873 0 0 0-1.115 2.693l.16.291c.415.764-.42 1.6-1.185 1.184l-.291-.159a1.873 1.873 0 0 0-2.693 1.116l-.094.318c-.246.835-1.428.835-1.674 0l-.094-.319a1.873 1.873 0 0 0-2.692-1.115l-.292.16c-.764.415-1.6-.42-1.184-1.185l.159-.291A1.873 1.873 0 0 0 1.945 8.93l-.319-.094c-.835-.246-.835-1.428 0-1.674l.319-.094A1.873 1.873 0 0 0 3.06 4.377l-.16-.292c-.415-.764.42-1.6 1.185-1.184l.292.159a1.873 1.873 0 0 0 2.692-1.115z"/>
    </svg>
  </button>

  @if (open()) {
    <!--
      Anchored under the gear and right-aligned to it. `w-72` with `max-w-[calc(100vw-2rem)]` keeps the panel
      inside a 360px phone viewport; `z-30` clears the page's sticky bands. It must NOT sit inside an
      `overflow-hidden` ancestor — see the note on `event-summary-card`, whose clipping was removed for this.
    -->
    <!--
      Side-aware anchoring: right-aligned to the gear by default, but when the gear sits near the LEFT edge
      (a wrapped header on a phone put it there once) a right-anchored panel runs off-screen, so `alignLeft`
      flips the anchor. Measured once per open in `openPanel()`.
    -->
    <!--
      `tabindex="-1"` makes the panel itself the focus target of a mousedown on its non-focusable content
      (label text, the explanation). Without it the browser focuses the NEAREST FOCUSABLE ANCESTOR instead —
      on the event-details page that is the `tabindex`-carrying `.page-scroller`, which is outside this
      component, so the tab-out rule in `onFocusOut` closed the panel before the click ever reached the
      label. With it, focus stays inside the host and only a real tab-out closes the panel.
    -->
    <div [id]="panelId()" role="dialog" tabindex="-1" [attr.aria-labelledby]="titleId()"
         (click)="onPanelClick($event)"
         [class.right-0]="!alignLeft()" [class.left-0]="alignLeft()"
         class="composition-grouping-panel absolute top-full z-30 mt-2 w-72 max-w-[calc(100vw-2rem)]
                rounded-2xl border border-gray-100 bg-white p-4 shadow-2xl outline-none">
      <p [id]="titleId()" class="text-sm font-semibold text-gray-900">Anzeigeoptionen</p>

      <!--
        ONE `<label>` wrapping both the text and the switch: it carries `for` AND contains the input, so the
        whole row is clickable while the control still has exactly one label element (two would trip axe's
        `form-field-multiple-labels`).
      -->
      <label [for]="switchId()" class="mt-3 flex cursor-pointer items-center justify-between gap-x-3">
        <span class="select-none text-sm text-gray-700">Kombinationen einzeln</span>
        <span class="relative inline-flex shrink-0 items-center">
          <input #groupingSwitch type="checkbox" class="peer sr-only" [id]="switchId()" role="switch"
                 [checked]="preference.individual()" [attr.aria-checked]="preference.individual()"
                 (change)="preference.toggle()">
          <span class="h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-0.5 after:top-0.5 after:h-5
                       after:w-5 after:rounded-full after:bg-white after:transition-all
                       peer-checked:bg-theme-primary-600 peer-checked:after:translate-x-5
                       peer-focus-visible:ring-2 peer-focus-visible:ring-theme-primary-500
                       peer-focus-visible:ring-offset-2"></span>
        </span>
      </label>

      <p class="mt-2 text-xs text-gray-500">
        Zeigt jede Kombination getauschter Bestandteile als eigene Zeile. Ein Sonderpreis erhält keine eigene
        Zeile.
      </p>
      <p class="mt-1 text-xs text-gray-400">Wird in diesem Browser gespeichert.</p>
    </div>
  }
</div>
